import AHDate from "_shared/dates/date";
import { useState } from "react";

type ForumTimelineRangeInput = {
  /** The scrubber is on screen. */
  active: boolean;
  /** A date the scrubber picked is filtering the list. */
  filtering: boolean;
  /** The list's own date range, from its x-pagination header. */
  listFirstDate: AHDate | undefined;
  listLastDate: AHDate | undefined;
  /** The list is fetching, so its range may belong to the previous query. */
  listLoading: boolean;
  /**
   * The newest message the list holds. Realtime and the user's own sends append messages to the
   * list without touching its x-pagination dates, so this is the only sign that the thread grew.
   */
  newestMessageDate: AHDate | undefined;
};

export type ForumTimelineRange = {
  firstDate: AHDate | undefined;
  lastDate: AHDate | undefined;
  /** Changes each time the scrubber opens afresh; key the scrubber on it to start it over. */
  generation: number;
};

type RangeState = ForumTimelineRange & {
  wasActive: boolean;
  awaitingList: boolean;
};

/**
 * The range the timeline scrubber spans, held still while the scrubber filters by it.
 *
 * The server computes a list's x-pagination dates over the filtered query, so once the scrubber
 * picks a month the list's range *is* that month. Passing the list's range straight through
 * remounted the scrubber on every pick: it lost its history, never showed Back, and opened the
 * month at its top level, unclipped. So the range is taken when the scrubber opens without a
 * filter, and kept until it next does.
 *
 * A list still loading when the scrubber opens may carry the range of the query before it, such as
 * the filter that was just cleared, so the range is taken again when that load finishes — unless a
 * pick has started filtering first.
 *
 * Held still is not frozen: a message newer than the range widens its end, filtering or not, so the
 * scrubber offers the day, week or month the message arrived in. Only the end moves, because
 * realtime only ever delivers new messages.
 */
export default function useForumTimelineRange(input: Readonly<ForumTimelineRangeInput>): ForumTimelineRange {
  const { active, filtering, listFirstDate, listLastDate, listLoading, newestMessageDate } = input;
  const [state, setState] = useState<RangeState>({
    firstDate: undefined,
    lastDate: undefined,
    generation: 0,
    wasActive: false,
    awaitingList: false,
  });

  // Adjusting state while rendering, React's pattern for reacting to a changed input without an
  // effect: the render that sees the change is thrown away and redone with the new range.
  if (active !== state.wasActive) {
    const opensAfresh = active && !filtering;
    setState({
      ...state,
      ...(opensAfresh && {
        ...spanning(listFirstDate, listLastDate, newestMessageDate),
        generation: state.generation + 1,
        awaitingList: listLoading,
      }),
      wasActive: active,
    });
  } else if (state.awaitingList && (filtering || !listLoading)) {
    setState({
      ...state,
      ...(!filtering && spanning(listFirstDate, listLastDate, newestMessageDate)),
      awaitingList: false,
    });
  } else if (state.generation > 0 && isLater(newestMessageDate, state.lastDate)) {
    setState({ ...state, ...spanning(state.firstDate, state.lastDate, newestMessageDate) });
  }

  return { firstDate: state.firstDate, lastDate: state.lastDate, generation: state.generation };
}

function isLater(date: AHDate | undefined, than: AHDate | undefined): date is AHDate {
  return !!date && (!than || date.isAfter(than));
}

// A list the server sent no dates for can still hold a message that arrived since.
function spanning(firstDate: AHDate | undefined, lastDate: AHDate | undefined, newest: AHDate | undefined) {
  return {
    firstDate: firstDate ?? newest,
    lastDate: isLater(newest, lastDate) ? newest : lastDate,
  };
}
